import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import { Datagrid, Error, Loading, useQuery } from 'react-admin';
import { Box, makeStyles } from '@material-ui/core';

import {
  AssignmentsProgressField,
  UnitProgressField,
  AssessmentsProgressField,
  LabsProgressField,
  QuizzesProgressField,
} from 'resources/common/cohorts/list/fields';
import { FullNameField, OutcomesStatusField } from 'resources/students/fields';
import {
  AssessmentScoreChip,
  AssignmentGradeDistributionField,
  ChipField,
  NewProgressField,
  OfflineList,
} from 'resources/common';
import { getQuizUnitId } from 'resources/common/quizProgress/quizPathUtils';

const useListStyles = makeStyles({
  units: { width: 100 },
  grades: { width: 250 },
  average_grade: { width: 100 },
  average_ai_score: { width: 100 },
  average_score: { width: 100 },
  average_quiz_score: { width: 120 },
});

const scoreMap = {
  very_good: 5,
  good: 4,
  normal: 3,
  bad: 2,
  very_bad: 1,
};

const isRequired = R.propEq('required', true);

const getCourseId = (record, course) => {
  const courseData = R.propOr({}, course, record);
  const openedxId = courseData.openedx || courseData.id || courseData.courseId;
  if (!openedxId) return course;

  return R.pipe(R.split(':'), R.drop(2), R.join(':'))(openedxId);
};

const toNumber = (value) => {
  if (R.isNil(value) || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const roundAverage = (values) => {
  const numbers = R.pipe(R.map(toNumber), R.reject(R.isNil))(values);
  if (R.isEmpty(numbers)) return null;

  return Math.round((R.mean(numbers) + Number.EPSILON) * 100) / 100;
};

const getProgressPercentage = ({ completed, total }) => {
  if (!total) return 0;
  return Math.round((100 * completed) / total);
};

const ProgressRatioField = ({ record, source }) => {
  const progress = R.pathOr({ completed: 0, total: 0 }, R.split('.', source), record);
  const completed = R.propOr(0, 'completed', progress);
  const total = R.propOr(0, 'total', progress);

  return (
    <NewProgressField
      value={getProgressPercentage({ completed, total })}
      text={`${completed}/${total}`}
    />
  );
};

const getProgressSorter = (course, progressField) =>
  R.pipe(
    R.pathOr({ completed: 0, total: 0 }, [course, progressField]),
    getProgressPercentage
  );

const normalizeEmail = R.pipe(R.defaultTo(''), String, R.toLower);

const matchesStudent = (student) => (record) => {
  const studentIds = R.reject(R.isNil, [
    student.id,
    student._id,
    student.studentId,
  ]);
  const recordIds = R.reject(R.isNil, [
    record.studentId,
    record.student_id,
    record.student,
    record.userId,
    record.user_id,
  ]);
  const hasMatchingId = R.any((id) => R.includes(id, studentIds), recordIds);
  const studentEmail = normalizeEmail(student.email);
  const recordEmail = normalizeEmail(record.email);

  return hasMatchingId || (studentEmail && studentEmail === recordEmail);
};

const getCompletedProgress = (items, isCompleted) => ({
  completed: R.pipe(R.filter(isCompleted), R.length)(items),
  total: R.length(items),
});

const combineProgress = R.reduce(
  (acc, progress) => ({
    completed: acc.completed + progress.completed,
    total: acc.total + progress.total,
  }),
  { completed: 0, total: 0 }
);

const getRequiredAssessmentsProgress = (challenges) =>
  R.pipe(
    R.groupBy(R.prop('assessment_id')),
    R.values,
    (assessments) => getCompletedProgress(assessments, R.any(R.propEq('status', 'delivered')))
  )(challenges);

const getRequiredTryHackMeLabs = R.pipe(
  R.propOr([], 'modules'),
  R.chain(
    R.pipe(R.propOr([], 'submodules'), R.chain(R.propOr([], 'labs')))
  ),
  R.filter(isRequired)
);

const getTryHackMeLabUnitIds = R.pipe(
  R.propOr([], 'modules'),
  R.chain(
    R.pipe(R.propOr([], 'submodules'), R.chain(R.propOr([], 'labs')))
  ),
  R.pluck('id'),
  R.reject(R.isNil)
);

const getStudentRecord = (student, records) =>
  R.find(matchesStudent(student), records || []);

const getRequiredQuizzesProgress = (student, quizzes) =>
  getCompletedProgress(quizzes, (quiz) =>
    Boolean(getStudentRecord(student, R.propOr([], 'submissions', quiz)))
  );

const getAverageQuizScore = (student, quizzes) =>
  R.pipe(
    R.map((quiz) => getStudentRecord(student, R.propOr([], 'submissions', quiz))),
    R.reject(R.isNil),
    R.pluck('globalScore'),
    roundAverage
  )(quizzes);

const getAverageQuizScoreRounded = (student, quizzes) =>
  R.pipe(
    R.map((quiz) => getStudentRecord(student, R.propOr([], 'submissions', quiz))),
    R.reject(R.isNil),
    R.pluck('globalScore'),
    R.map(toNumber),
    R.reject(R.isNil),
    (scores) => (R.isEmpty(scores) ? null : Math.round(R.mean(scores)))
  )(quizzes);

const getQuizzesForProgress = (quizzes) =>
  R.when(R.any(isRequired), R.filter(isRequired))(quizzes);

const isTryHackMeLabCompleted = (studentProgress) => {
  if (!studentProgress) return false;
  const total = R.propOr(0, 'tasksTotal', studentProgress);
  const completed = R.propOr(0, 'tasksCompleted', studentProgress);

  return (
    R.propEq('status', 'completed', studentProgress) ||
    Boolean(R.prop('completedAt', studentProgress)) ||
    (total > 0 && completed >= total)
  );
};

const getRequiredTryHackMeProgress = (student, labs) =>
  getCompletedProgress(labs, (lab) =>
    isTryHackMeLabCompleted(getStudentRecord(student, R.propOr([], 'students', lab)))
  );

const getAverageRequiredTryHackMeScore = (student, labs) =>
  R.pipe(
    R.map((lab) => getStudentRecord(student, R.propOr([], 'students', lab))),
    R.reject(R.isNil),
    R.pluck('score'),
    roundAverage
  )(labs);

const ByStudentsSubtab = (props) => {
  const { record, course } = props;
  const classes = useListStyles();
  const [students, setStudents] = useState([]);

  const assignments = R.pathOr([], [course, 'assignments'], record);
  const assessments = R.pipe(
    R.pathOr([], [course, 'challenges']),
    R.uniqBy(R.prop('assessment_id'))
  )(record);
  const labs = R.pathOr([], [course, 'labs'], record);
  const hasQuizzes = R.hasPath([course, 'quizzes'], record);
  const isCybersecurityCourseProgress = record.track === 'cy' && course === 'course';
  const courseId = getCourseId(record, course);

  const {
    data: apiStudents,
    loading: studentsLoading,
    error: studentsError,
  } = useQuery({
    type: 'getManyReference',
    resource: 'students',
    payload: {
      target: 'cohort',
      id: record.id,
      pagination: {
        perPage: 100,
      },
      filter: { course: true },
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  const {
    data: apiQuizzes,
    loading: quizzesLoading,
    error: quizzesError,
  } = useQuery(
    {
      type: 'getManyReference',
      resource: 'quizzes',
      payload: {
        target: 'cohort',
        id: record.id,
        pagination: {
          perPage: 100,
        },
        filter: { course },
        sort: { field: 'first_name', order: 'ASC' },
      },
    },
    { enabled: isCybersecurityCourseProgress || hasQuizzes }
  );

  const {
    data: tryHackMeLabsData,
    loading: tryHackMeLabsLoading,
    error: tryHackMeLabsError,
  } = useQuery(
    {
      type: 'getOne',
      resource: 'tryhackme_labs',
      payload: {
        id: record.id,
        courseId,
      },
    },
    { enabled: isCybersecurityCourseProgress }
  );

  useEffect(() => {
    if (apiStudents) {
      const hiddenUnits = R.pathOr([], [course, 'hidden_units'], record);
      const isUnitHidden = (item) =>
        R.includes(getQuizUnitId(item), hiddenUnits);
      const getVisibleRequired = R.pipe(
        R.reject(isUnitHidden),
        R.filter(isRequired)
      );
      const visibleQuizzes = R.reject(isUnitHidden, apiQuizzes || []);
      const quizzesForProgress = getQuizzesForProgress(visibleQuizzes);
      const requiredQuizzes = isCybersecurityCourseProgress
        ? R.filter(isRequired, visibleQuizzes)
        : [];
      const requiredTryHackMeLabs = isCybersecurityCourseProgress
        ? getRequiredTryHackMeLabs(tryHackMeLabsData || {})
        : [];
      const tryHackMeLabUnitIds = isCybersecurityCourseProgress
        ? getTryHackMeLabUnitIds(tryHackMeLabsData || {})
        : [];
      const isTryHackMeAssignment = (assignment) =>
        R.includes(R.path(['path', 3, 'id'], assignment), tryHackMeLabUnitIds);
      const tableData = R.pipe(
        R.map((student) => {
          const averageGrade = R.pipe(
            R.pathOr([], [course, 'deliveries']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.pluck('grade'),
            R.reject(R.isNil),
            R.map(R.prop(R.__, scoreMap)),
            (grades) =>
              R.defaultTo(
                null,
                Math.round((R.mean(grades) + Number.EPSILON) * 100) / 100
              )
          )(student);
          const averageScore = R.pipe(
            R.pathOr([], [course, 'challenges']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.uniqBy(R.prop('assessment_id')),
            R.pluck('assessment_score_average'),
            R.reject(R.isNil),
            (averages) =>
              R.defaultTo(
                null,
                Math.round((R.mean(averages) + Number.EPSILON) * 100) / 100
              )
          )(student);

          const averageAiScore = R.pipe(
            R.pathOr([], [course, 'labs']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.map((lab) => {
              const lastAttempt = R.last(R.propOr([], 'attempts', lab));
              return R.pipe(
                R.propOr([], 'evaluables'),
                R.reject(R.isNil),
                R.pluck('score'),
                (scores) =>
                  R.defaultTo(
                    null,
                    Math.round((R.mean(scores) + Number.EPSILON) * 100) / 100
                  )
              )(lastAttempt);
            }),
            R.reject(R.isNil),
            (averages) =>
              R.defaultTo(
                null,
                Math.round((R.mean(averages) + Number.EPSILON) * 100) / 100
              )
          )(student);

          const averageCutScore = R.pipe(
            R.pathOr([], [course, 'challenges']),
            R.when(R.any(isRequired), R.filter(isRequired)),
            R.uniqBy(R.prop('assessment_id')),
            R.pluck('assessment_cut_score'),
            R.reject(R.isNil),
            R.mean,
            (mean) => Math.round((mean + Number.EPSILON) * 100) / 100
          )(student);

          const requiredAssignments = R.pipe(
            R.pathOr([], [course, 'deliveries']),
            R.reject(isTryHackMeAssignment),
            getVisibleRequired
          )(student);
          const requiredAssignmentsProgress = getCompletedProgress(
            requiredAssignments,
            R.pipe(R.propOr('', 'status'), R.startsWith('delivered'))
          );
          const requiredChallenges = R.pipe(
            R.pathOr([], [course, 'challenges']),
            getVisibleRequired
          )(student);
          const requiredAssessmentsProgress = getRequiredAssessmentsProgress(
            requiredChallenges
          );
          const requiredQuizProgress = getRequiredQuizzesProgress(
            student,
            requiredQuizzes
          );
          const requiredTryHackMeProgress = getRequiredTryHackMeProgress(
            student,
            requiredTryHackMeLabs
          );
          const requiredAssessmentAverages = R.pipe(
            R.uniqBy(R.prop('assessment_id')),
            (requiredAssessments) => ({
              score: roundAverage(
                R.pluck('assessment_score_average', requiredAssessments)
              ),
              cutScore: roundAverage(
                R.pluck('assessment_cut_score', requiredAssessments)
              ),
            })
          )(requiredChallenges);

          return R.mergeDeepRight(student, {
            [course]: {
              average_ai_score: averageAiScore,
              average_cut_score: averageCutScore,
              average_grade: averageGrade,
              average_score: averageScore,
              required_assessment_average_cut_score:
                requiredAssessmentAverages.cutScore,
              required_assessment_average_score: requiredAssessmentAverages.score,
              required_deliverables: combineProgress([
                requiredAssignmentsProgress,
                requiredAssessmentsProgress,
                requiredQuizProgress,
                requiredTryHackMeProgress,
              ]),
              required_quiz_average_score: getAverageQuizScore(
                student,
                visibleQuizzes
              ),
              average_quiz_score: getAverageQuizScoreRounded(
                student,
                quizzesForProgress
              ),
              required_tryhackme_average_score:
                getAverageRequiredTryHackMeScore(student, requiredTryHackMeLabs),
              required_tryhackme_deliverables: requiredTryHackMeProgress,
            },
          });
        })
      )(apiStudents);
      setStudents(tableData);
    }
  }, [
    apiQuizzes,
    apiStudents,
    course,
    isCybersecurityCourseProgress,
    record,
    tryHackMeLabsData,
  ]);

  const loading =
    studentsLoading ||
    (hasQuizzes && quizzesLoading) ||
    (isCybersecurityCourseProgress && tryHackMeLabsLoading);
  const progressError =
    studentsError ||
    (hasQuizzes && quizzesError) ||
    (isCybersecurityCourseProgress &&
    tryHackMeLabsError &&
    tryHackMeLabsError.status !== 404
      ? tryHackMeLabsError
      : null);

  if (loading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (progressError)
    return (
      <Box p="16px">
        <Error error={progressError} errorInfo={progressError} />
      </Box>
    );

  if (R.isEmpty(students)) {
    return null;
  }

  return (
    <OfflineList
      list={students}
      uniqueKey="id"
      defaultSort={{ field: 'first_name', order: 'ASC' }}
      nullsLastFields={[`${course}.average_quiz_score`]}
      sorters={{
        units_progress: R.pipe(R.pathOr([], [course, 'progress']), R.length),
        assignments_progress: R.pipe(
          R.pathOr([], [course, 'deliveries']),
          R.filter(R.pipe(R.propOr('', 'status'), R.startsWith('delivered'))),
          R.length
        ),
        assessments_progress: R.pipe(
          R.pathOr([], [course, 'challenges']),
          R.filter(R.propEq('status', 'delivered')),
          R.length
        ),
        [`${course}.deliveries`]: R.pipe(
          R.pathOr([], [course, 'deliveries']),
          R.filter(R.propEq('grade', 'very_good')),
          R.length
        ),
        [`${course}.required_deliverables`]: getProgressSorter(
          course,
          'required_deliverables'
        ),
        [`${course}.required_tryhackme_deliverables`]: getProgressSorter(
          course,
          'required_tryhackme_deliverables'
        ),
        [`${course}.required_tryhackme_average_score`]: R.pathOr(0, [
          course,
          'required_tryhackme_average_score',
        ]),
        [`${course}.required_quiz_average_score`]: R.pathOr(0, [
          course,
          'required_quiz_average_score',
        ]),
        [`${course}.required_assessment_average_score`]: R.pathOr(0, [
          course,
          'required_assessment_average_score',
        ]),
        [`${course}.average_quiz_score`]: R.path([course, 'average_quiz_score']),
      }}
    >
      <Datagrid
        rowClick={(id) => {
          window.open(`/#/students/${id}/show/${course}_progress`, '_blank');
        }}
      >
        <FullNameField />
        {!isCybersecurityCourseProgress && <OutcomesStatusField />}
        {R.pathOr(0, [course, 'learning_units_count'], record) > 0 && (
          <UnitProgressField
            label="Unit Progress"
            source="units_progress"
            course={course}
            cohort={record}
            cellClassName={classes.units}
          />
        )}
        {isCybersecurityCourseProgress && (
          <ProgressRatioField
            label="Required Deliverables"
            source={`${course}.required_deliverables`}
          />
        )}
        {isCybersecurityCourseProgress && (
          <ProgressRatioField
            label="THM Deliverables"
            source={`${course}.required_tryhackme_deliverables`}
          />
        )}
        {isCybersecurityCourseProgress && (
          <ChipField
            label="Average THM Lab Score"
            source={`${course}.required_tryhackme_average_score`}
            chipStyle="labGrade"
            cellClassName={classes.average_score}
          />
        )}
        {isCybersecurityCourseProgress && (
          <ChipField
            label="Average Quiz Score"
            source={`${course}.required_quiz_average_score`}
            chipStyle="labGrade"
            cellClassName={classes.average_score}
          />
        )}
        {isCybersecurityCourseProgress && (
          <AssessmentScoreChip
            label="Average Assessment Score"
            scoreSource={`${course}.required_assessment_average_score`}
            cutScoreSource={`${course}.required_assessment_average_cut_score`}
          />
        )}
        {!isCybersecurityCourseProgress && (
          <AssignmentsProgressField
            label={`${
              R.any(R.propEq('required', true), assignments) ? 'Req. ' : ''
            }Assignments`}
            source="assignments_progress"
            course={course}
            cohort={record}
          />
        )}
        {!isCybersecurityCourseProgress && R.hasPath([course, 'labs'], record) && (
          <LabsProgressField
            label={`${R.any(R.propEq('required', true), labs) ? 'Req. ' : ''}Labs`}
            source="labs"
            course={course}
            cohort={record}
          />
        )}
        {!isCybersecurityCourseProgress && hasQuizzes && (
          <QuizzesProgressField
            label={`${
              R.any(R.propEq('required', true), apiQuizzes || []) ? 'Req. ' : ''
            }Quizzes`}
            source="quizzes"
            course={course}
            cohort={record}
            quizzes={apiQuizzes || []}
          />
        )}
        {!isCybersecurityCourseProgress && hasQuizzes && (
          <ChipField
            label="Avg. Quiz Score"
            source={`${course}.average_quiz_score`}
            chipStyle="labGrade"
            emptyLabel="—"
            cellClassName={classes.average_quiz_score}
          />
        )}
        {!isCybersecurityCourseProgress && (
          <AssignmentGradeDistributionField
            label="Grades"
            source={`${course}.deliveries`}
            sortable={false}
            cellClassName={classes.grades}
          />
        )}
        {!isCybersecurityCourseProgress && (
          <ChipField
            label="Avg. Grade"
            source={`${course}.average_grade`}
            chipStyle="assignmentGrade"
            cellClassName={classes.average_grade}
          />
        )}
        {!isCybersecurityCourseProgress && (
          <AssessmentsProgressField
            label={`${
              R.any(R.propEq('required', true), assessments)
                ? 'Req. Passed '
                : ''
            }Assessments`}
            course={course}
            cohort={record}
            source="assessments_progress"
          />
        )}
        {!isCybersecurityCourseProgress && (
          <AssessmentScoreChip
            label="Avg. Score"
            scoreSource={`${course}.average_score`}
            cutScoreSource={`${course}.average_cut_score`}
          />
        )}
        {!isCybersecurityCourseProgress && R.hasPath([course, 'labs'], record) && (
          <ChipField
            label="Avg. AI Score"
            source={`${course}.average_ai_score`}
            chipStyle="labGrade"
            cellClassName={classes.average_ai_score}
          />
        )}
      </Datagrid>
    </OfflineList>
  );
};

export default ByStudentsSubtab;
